import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
// Components
import Image from '../Image';
import TopBar from './TopBar';
import Button from '../button';
// Interfaces
import { Menu as MenuInterface } from '../../interfaces/menu';
// utils
import { slugManager } from '../../utils/SlugManager';

interface HeaderProps {
  pageId?: string | undefined;
  locale: string;
  menuItems: MenuInterface;
}

const Header = (props: HeaderProps) => {
  const [isToggleOpen, setIsToggleOpen] = useState(false);
  const [isOnMobileTablet, setIsOnMobileTablet] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const { topBarText, topBarLeft, topBarRight, logo, menuItems, callToActionMenuItems } =
    props.menuItems;

  const [isMenuItemOpen, setMenuItemOpen] = useState(false);

  useEffect(() => {
    const userAgent = window?.navigator.userAgent;
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );

    setIsOnMobileTablet(isMobileOrTablet);
  }, [isToggleOpen]);

  return (
    <React.Fragment>
      <TopBar
        message={topBarText}
        leftMenu={topBarLeft}
        rightMenu={topBarRight}
        pageId={props.pageId && props.pageId}
      />

      <header className='cl-header'>
        <div className='cl-container'>
          <nav aria-label='Circleloop' className='cl-navigation'>
            {/* <!-- Logo --> */}
            <Link to={slugManager('/', props.locale)} className='cl-navigation__brand'>
              <Image data={logo} />
            </Link>
            {/* <!-- Menu Toggler --> */}
            <button
              onClick={() => setIsToggleOpen(!isToggleOpen)}
              className={
                isToggleOpen
                  ? 'cl-navigation__toggler cl-navigation__toggler--open'
                  : 'cl-navigation__toggler'
              }
              aria-expanded='false'
              aria-label='Toggle navigation'
              aria-controls='menu'
            >
              <div className='cl-navigation__toggler-container'>
                <span aria-hidden='true' className='cl-navigation__toggler-icon'></span>
                <span aria-hidden='true' className='cl-navigation__toggler-icon'></span>
                <span aria-hidden='true' className='cl-navigation__toggler-icon'></span>
              </div>
            </button>
            {/* <!-- Menu --> */}
            <ul role='menu' aria-label='Circleloop' className='cl-navigation__menu' id='menu'>
              {/* <!-- Main Menu Items --> */}
              {menuItems.map((mainMenuItem, index: number) => (
                <li role='none' className='cl-navigation__menu-item' key={index}>
                  <Link
                    className='cl-navigation__menu-link'
                    // activeClassName={`${
                    //   activeMenuItem === index ? 'cl-navigation__menu-link--active' : ''
                    // }`}
                    activeClassName='cl-navigation__menu-link--active'
                    role='menuitem'
                    onClick={() => {
                      setActiveMenuItem(index);
                      setMenuItemOpen(!isMenuItemOpen);
                    }}
                    to={slugManager(
                      mainMenuItem.menuLink.externalUrl || mainMenuItem.menuLink.linkedPage.slug,
                      props.locale,
                    )}
                    aria-haspopup={mainMenuItem.columns ? 'true' : 'false'}
                    tabIndex={0}
                    data-id={index}
                  >
                    {mainMenuItem.menuLink.linkName}

                    {mainMenuItem.columns && (
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M4 6L7.81156 9.8112C7.83629 9.83597 7.86566 9.85562 7.898 9.86903C7.93033 9.88243 7.965 9.88933 8 9.88933C8.035 9.88933 8.06967 9.88243 8.102 9.86903C8.13434 9.85562 8.16371 9.83597 8.18844 9.8112L12 6'
                          stroke='inherit'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </Link>

                  {/* <!-- Menu Item Column Items --> */}
                  {mainMenuItem.columns && (
                    <div
                      className={`cl-navigation__menu-dropdown cl-navigation__menu-dropdown--${
                        mainMenuItem.dropdownPosition
                      } ${isOnMobileTablet && isMenuItemOpen ? 'visible block' : ''}`}
                      role='menu'
                      aria-label='aria LAbel'
                    >
                      {mainMenuItem.columns.map((column, columnIndex: number) => (
                        <div className='cl-navigation__menu-dropdown-col' key={columnIndex}>
                          <ul role='none'>
                            {column.columnItems.map((menuItem, menuItemIndex: number) => (
                              <li
                                role='none'
                                key={menuItemIndex}
                                className={`cl-navigation__menu-dropdown-item ${
                                  menuItem.menuLink.leadingIcon
                                    ? 'cl-navigation__menu-dropdown-item-icon'
                                    : ''
                                }`}
                              >
                                <Link
                                  className={`cl-navigation__menu-dropdown-link ${
                                    menuItem.linkType === 'Header link'
                                      ? 'cl-navigation__menu-dropdown-link--header'
                                      : menuItem.linkType === 'Read more link'
                                      ? 'cl-navigation__menu-dropdown-link--more'
                                      : ''
                                  }`}
                                  role='menuitem'
                                  to={slugManager(
                                    menuItem.menuLink.externalUrl ||
                                      menuItem.menuLink.linkedPage.slug,
                                    props.locale,
                                  )}
                                  data-id={menuItemIndex}
                                >
                                  {menuItem.menuLink.leadingIcon && (
                                    <Image data={menuItem.menuLink.leadingIcon} />
                                  )}

                                  {menuItem.menuLink.linkName}

                                  {menuItem.menuLink.trailingIcon && (
                                    <Image data={menuItem.menuLink.trailingIcon} />
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {/* Call To Action Buttons  */}
            <div className='cl-navigation-actions'>
              {callToActionMenuItems && (
                <ul className='cl-navigation-actions__buttons'>
                  {callToActionMenuItems.map((cta, index) => (
                    <li role='none' className='cl-navigation__actions-item' key={index}>
                      <Button data={cta} />
                    </li>
                  ))}
                </ul>
              )}
              <TopBar
                message={topBarText}
                leftMenu={topBarLeft}
                rightMenu={topBarRight}
                pageId={props.pageId && props.pageId}
                mobileNav
              />
            </div>
          </nav>
        </div>
      </header>
    </React.Fragment>
  );
};

export default Header;
